import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import { CalendarIcon, UserIcon } from '@heroicons/react/outline';
import {
  getMedicationStatusLabel,
  getRecordColor,
} from '../../../screens/EpisodesOfCare/helpers';
import useForm from '../../../utils/useForm';
import MetaDataItem from './MetaDataItem';
import EpisodeOfCareDetails from './EpisodeOfCareDetails';

const MedicationDetails = ({ medication, visible, onCancel, isEditable }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { context = {} } = medication;

  const milestonesOptions = [
    {
      label: 'Baseline',
      value: 'milestone1',
    },
    {
      label: 'Initial Assessment',
      value: 'milestone2',
    },
  ];

  const { form, isValid, handleSubmit, renderField } = useForm({
    inputs: {
      patientName: {
        testId: 'form-field-patient-name',
        label: t('forms:recipientName.label'),
        type: 'text',
        defaultValue: context?.patientName,
        showValueOnly: !isEditable,
        muted: true,
        disabled: isSubmitting,
        schema: {
          type: String,
        },
      },
      patientId: {
        testId: 'form-field-patient-id',
        label: t('forms:recipientId.label'),
        type: 'text',
        defaultValue: medication.patientId,
        showValueOnly: !isEditable,
        muted: true,
        disabled: isSubmitting,
        schema: {
          type: String,
        },
      },
      projectId: {
        testId: 'form-field-project-id',
        label: t('forms:projectId.label'),
        type: 'text',
        defaultValue: context?.projectId,
        showValueOnly: !isEditable,
        muted: true,
        disabled: isSubmitting,
        schema: {
          type: String,
          optional: true,
        },
      },
      milestoneId: {
        testId: 'form-field-roles',
        label: t('milestones', { count: 1 }),
        type: 'react-select',
        options: milestonesOptions,
        // defaultValue: context?.milestoneId,
        showValueOnly: !isEditable,
        muted: true,
        disabled: isSubmitting,
        schema: {
          type: String,
          optional: true,
        },
      },
    },
    onSubmit: (values) => {
      console.log(values);

      // setIsSubmitting(true);

      // setTimeout(() => {
      setIsSubmitting(false);
      // onCancel();
      // }, 1000);
    },
  });

  const handleSaveAndProcess = () => {
    console.log(form);

    // setIsSubmitting(true);

    // setTimeout(() => {
    // setIsSubmitting(false);
    onCancel();
    // }, 1000);
  };

  return (
    <EpisodeOfCareDetails
      title={t('details')}
      visible={visible}
      onCancel={onCancel}
      onSave={handleSubmit}
      onSaveAndProcess={handleSaveAndProcess}
      isValid={isValid}
      isEditable={isEditable}
    >
      <div className="space-y-4">
        <h2 className="text-lg font-normal">{t('personalInfo')}</h2>

        <div className="grid grid-cols-2 gap-4">
          {renderField('patientId')}
          {renderField('patientName')}
          {renderField('projectId')}
          {isEditable ? (
            renderField('milestoneId')
          ) : (
            <MetaDataItem
              label={t('milestones', { count: 1 })}
              content="Baseline"
            />
          )}
        </div>

        <hr className="border-divider" />

        <h2 className="text-lg font-normal">{t('details')}</h2>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            label={t('forms:status.label')}
            content={
              <span
                className={`${getRecordColor(
                  medication.status,
                )} text-xs px-2 py-1 rounded`}
              >
                {getMedicationStatusLabel({ status: medication.status, t })}
              </span>
            }
          />
          <MetaDataItem
            label={t('date')}
            content={toIntlDateTimeFormat(language, medication.start)}
            icon={<CalendarIcon className="w-4 h-4" />}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem label={t('forms:name.label')} content={context.name} />
          <MetaDataItem
            label={t('forms:dosage.label')}
            content={context.dosage}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem label={t('forms:unit.label')} content={context.unit} />
          <MetaDataItem
            label={t('forms:frequency.label')}
            content={context.frequency}
          />
        </div>

        <hr className="border-divider" />

        <MetaDataItem
          label={t('forms:clinician.label')}
          content={context.clinicianName}
          icon={<UserIcon className="w-4 h-4" />}
        />
        <MetaDataItem
          label={t('notes')}
          content={
            <div className="bg-neutral-400 p-2 rounded mt-1">
              {context.notes}
            </div>
          }
        />
        <MetaDataItem
          label={t('forms:identifier.label')}
          content={medication.eventId}
        />
        <MetaDataItem label={t('type')} content="Medication" />
      </div>
    </EpisodeOfCareDetails>
  );
};

MedicationDetails.propTypes = {
  medication: PropTypes.shape({
    patientId: PropTypes.string,
    eventId: PropTypes.string,
    start: PropTypes.instanceOf(Date),
    status: PropTypes.string,
    context: PropTypes.shape({
      patientName: PropTypes.string,
      projectId: PropTypes.string,
      notes: PropTypes.string,
      unit: PropTypes.string,
      dosage: PropTypes.string,
      frequency: PropTypes.string,
    }),
  }),
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  isEditable: PropTypes.bool,
};

MedicationDetails.defaultProps = {
  medication: {},
  visible: false,
  onCancel: () => {},
  isEditable: false,
};

export default MedicationDetails;
